import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'

import {
  AdminFormDto,
  FormAuthType,
  FormLanguage,
  FormResponseMode,
  FormSchedule,
  FormSettings,
  FormStatus,
} from '~shared/types/form/form'
import { TwilioCredentials } from '~shared/types/twilio'

import { ApiError } from '~typings/core'

import { GUIDE_PREVENT_EMAIL_BOUNCE } from '~constants/links'
import { useToast } from '~hooks/useToast'
import { getFormLanguageTranslation } from '~components/Dropdown/utils/getItemSelectionTranslation'

import { adminFormKeys } from '../common/queries'

import { adminFormSettingsKeys } from './queries'
import {
  deleteTwilioCredentials,
  updateFormAuthType,
  updateFormCaptcha,
  updateFormEmails,
  updateFormEsrvcId,
  updateFormInactiveMessage,
  updateFormLanguage,
  updateFormLimit,
  updateFormSchedule,
  updateFormStatus,
  updateFormTitle,
  updateFormWebhookRetries,
  updateFormWebhookUrl,
  updateTwilioCredentials,
} from './SettingsService'

export const useMutateFormSettings = () => {
  const { formId } = useParams()
  if (!formId) throw new Error('No formId provided')

  const i18n = useTranslation()

  const queryClient = useQueryClient()
  const toast = useToast({ status: 'success', isClosable: true })
  const formSettingsQueryKey = adminFormSettingsKeys.id(formId)

  const updateFormData = useCallback(
    (newData: FormSettings) => {
      queryClient.setQueryData(adminFormSettingsKeys.id(formId), newData)
      // Only update adminForm if it already has prior data.
      queryClient.setQueryData<AdminFormDto | undefined>(
        adminFormKeys.id(formId),
        (oldData) =>
          oldData
            ? {
                ...oldData,
                ...newData,
              }
            : undefined,
      )
    },
    [formId, queryClient],
  )

  const handleSuccess = useCallback(
    ({
      newData,
      toastDescription,
    }: {
      newData: FormSettings
      toastDescription: string
    }) => {
      toast.closeAll()
      updateFormData(newData)
      toast({
        description: toastDescription,
      })
    },
    [toast, updateFormData],
  )

  const handleError = useCallback(
    (error: Error) => {
      toast.closeAll()
      let errorMsg = ''
      if (error.message === 'Validation failed') {
        errorMsg = i18n.t(
          'features.adminForm.settings.mutations.useMutateFormSettings.validationFailed',
        )
      } else if (
        error.message.startsWith(
          'Error: [Error encountered during DNS resolution for webhook URL',
        )
      ) {
        const urlRegex = /(https:\/\/[^\s.]+\.[^\s]+(?:\/[^\s]*)?)(?<!\.)/g
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
        errorMsg = error.message
        const urls = [...errorMsg.matchAll(urlRegex)].map((match) => match[0])
        const emailMatches = [...errorMsg.matchAll(emailRegex)].map(
          (match) => match[0],
        )
        errorMsg = i18n.t(
          'features.adminForm.settings.mutations.useMutateFormSettings.validationFailedMessage.webhookURL',
          {
            webhookURL: urls[0],
            contactEmail: emailMatches[0],
          },
        )
      } else {
        errorMsg = error.message
      }
      toast({
        description: errorMsg,
        status: 'danger',
      })
    },
    [i18n, toast],
  )
  const mutateFormStatus = useMutation(
    (nextStatus: FormStatus) => updateFormStatus(formId, nextStatus),
    {
      onSuccess: (newData) => {
        // Show toast on success.
        const isNowPublic = newData.status === FormStatus.Public
        const toastStatusPublicMessage =
          newData.responseMode === FormResponseMode.Encrypt
            ? `${i18n.t('settingMutations.formOpen')}\n\n${i18n.t(
                'settingMutations.storeSecretKey',
              )}`
            : `${i18n.t('settingMutations.formOpen')}\n\n${i18n.t(
                'settingMutations.largeResponse',
              )} [${i18n.t(
                'settingMutations.autoArchive',
              )}](${GUIDE_PREVENT_EMAIL_BOUNCE}) ${i18n.t(
                'settingMutations.avoidLost',
              )}`
        const toastStatusClosedMessage = i18n.t('settingMutations.formClose')
        const toastStatusMessage = isNowPublic
          ? toastStatusPublicMessage
          : toastStatusClosedMessage

        handleSuccess({ newData, toastDescription: toastStatusMessage })
      },
      onError: handleError,
    },
  )

  const mutateFormLimit = useMutation(
    (nextLimit: number | null) => updateFormLimit(formId, nextLimit),
    {
      onSuccess: (newData) => {
        // Show toast on success.
        const toastStatusMessage = newData.submissionLimit
          ? i18n.t('settingMutations.autoFormClose', {
              formLimit: newData.submissionLimit,
            })
          : i18n.t('settingMutations.submissionLimit')
        handleSuccess({ newData, toastDescription: toastStatusMessage })
      },
      onError: handleError,
    },
  )

  const mutateFormSchedule = useMutation(
    (schedule: FormSchedule) => updateFormSchedule(formId, schedule),
    {
      onSuccess: (newData) => {
        // Show toast on success.
        const toastStatusMessage = newData.schedule?.isEnable
          ? i18n.t('features.adminForm.settings.mutation.formOpenCloseOn')
          : i18n.t('features.adminForm.settings.mutation.formOpenCloseOff')
        handleSuccess({ newData, toastDescription: toastStatusMessage })
      },
      onError: handleError,
    },
  )

  const mutateFormUpdateSchedule = useMutation(
    (schedule: FormSchedule) => updateFormSchedule(formId, schedule),
    {
      onSuccess: (newData) => {
        const toastStatusUpdateMessage = i18n.t(
          'features.adminForm.settings.mutation.formOpenUpdated',
        )
        handleSuccess({ newData, toastDescription: toastStatusUpdateMessage })
      },
      onError: handleError,
    },
  )

  const mutateFormLanguage = useMutation(
    (nextLanguage: FormLanguage) => updateFormLanguage(formId, nextLanguage),
    {
      onSuccess: (newData) => {
        toast.closeAll()
        // Update new settings data in cache.
        updateFormData(newData)

        // Show toast on success.
        toast({
          description: i18n.t(
            'features.adminForm.settings.mutations.useMutateFormSettings.mutateFormLanguage.formLanguageHasBeenUpdatedTo',
            {
              newFormLang: getFormLanguageTranslation(newData.lang),
            },
          ),
        })
      },
      onError: handleError,
    },
  )

  const mutateFormCaptcha = useMutation(
    (nextHasCaptcha: boolean) => updateFormCaptcha(formId, nextHasCaptcha),
    {
      onSuccess: (newData) => {
        handleSuccess({
          newData,
          toastDescription: `${i18n.t('settingMutations.recaptcha')}${
            newData.hasCaptcha
              ? i18n.t('settingMutations.enable')
              : i18n.t('settingMutations.disable')
          }${i18n.t('settingMutations.onYourForm')}`,
        })
      },
      onError: handleError,
    },
  )

  const mutateFormTitle = useMutation(
    (nextTitle: string) => updateFormTitle(formId, nextTitle),
    {
      onSuccess: (newData) => {
        toast.closeAll()
        // Update new settings data in cache.
        updateFormData(newData)

        // Show toast on success.
        toast({
          description: i18n.t('settingMutations.titleUpdate'),
        })
      },
      onError: (error: Error) => {
        toast.closeAll()
        toast({
          description: error.message,
          status: 'danger',
        })
      },
    },
  )

  const mutateFormInactiveMessage = useMutation(
    (nextMessage: string) => updateFormInactiveMessage(formId, nextMessage),
    {
      onSuccess: (newData) => {
        handleSuccess({
          newData,
          toastDescription: i18n.t('settingMutations.inactive'),
        })
      },
      onError: handleError,
    },
  )

  const mutateFormEmails = useMutation(
    (nextEmails: string[]) => updateFormEmails(formId, nextEmails),
    {
      onSuccess: (newData) => {
        handleSuccess({
          newData,
          toastDescription: i18n.t('settingMutations.email'),
        })
      },
      onError: handleError,
    },
  )

  const mutateFormEsrvcId = useMutation(
    (nextEsrvcId?: string) => updateFormEsrvcId(formId, nextEsrvcId),
    {
      onSuccess: (newData) => {
        handleSuccess({
          newData,
          toastDescription: i18n.t('settingMutations.eservice'),
        })
      },
      onError: handleError,
    },
  )

  const mutateFormAuthType = useMutation<
    FormSettings,
    ApiError,
    FormAuthType,
    { previousSettings?: FormSettings }
  >((nextAuthType: FormAuthType) => updateFormAuthType(formId, nextAuthType), {
    // Optimistic update
    onMutate: async (newData) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(formSettingsQueryKey)

      // Snapshot the previous value
      const previousSettings =
        queryClient.getQueryData<FormSettings>(formSettingsQueryKey)

      // Optimistically update to the new value
      queryClient.setQueryData<FormSettings | undefined>(
        formSettingsQueryKey,
        (old) => {
          if (!old) return
          return {
            ...old,
            authType: newData,
          }
        },
      )

      // Return a context object with the snapshotted value
      return { previousSettings }
    },
    onSuccess: (newData) => {
      handleSuccess({
        newData,
        toastDescription: i18n.t('settingMutations.formAuth'),
      })
    },
    onError: (error, _newData, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(formSettingsQueryKey, context.previousSettings)
      }
      handleError(error)
    },
    onSettled: (_data, error) => {
      if (error) {
        // Refetch data if any error occurs
        queryClient.invalidateQueries(formSettingsQueryKey)
      }
    },
  })

  const mutateFormWebhookUrl = useMutation(
    (nextUrl?: string) => updateFormWebhookUrl(formId, nextUrl),
    {
      onSuccess: (newData, nextUrl) => {
        handleSuccess({
          newData,
          toastDescription: `${i18n.t('settingMutations.webhook')}${
            nextUrl
              ? i18n.t('settingMutations.update')
              : i18n.t('settingMutations.remove')
          }`,
        })
      },
      onError: handleError,
    },
  )

  const mutateWebhookRetries = useMutation(
    (nextEnabled: boolean) => updateFormWebhookRetries(formId, nextEnabled),
    {
      onSuccess: (newData, nextEnabled) => {
        handleSuccess({
          newData,
          toastDescription: `${i18n.t('settingMutations.webhookRetry')}${
            nextEnabled
              ? i18n.t('settingMutations.enableWithDot')
              : i18n.t('settingMutations.disableWithDot')
          }`,
        })
      },
      onError: handleError,
    },
  )

  return {
    mutateWebhookRetries,
    mutateFormWebhookUrl,
    mutateFormStatus,
    mutateFormLimit,
    mutateFormInactiveMessage,
    mutateFormCaptcha,
    mutateFormEmails,
    mutateFormTitle,
    mutateFormAuthType,
    mutateFormEsrvcId,
    mutateFormSchedule,
    mutateFormUpdateSchedule,
    mutateFormLanguage,
  }
}

export const useMutateTwilioCreds = () => {
  const { formId } = useParams()
  if (!formId) throw new Error('No formId provided')
  const i18n = useTranslation()
  const queryClient = useQueryClient()
  const toast = useToast({ status: 'success', isClosable: true })

  const mutateFormTwilioDetails = useMutation(
    (credentials: TwilioCredentials) =>
      updateTwilioCredentials(formId, credentials),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(adminFormKeys.id(formId))
        toast.closeAll()
        // Show toast on success.
        toast({
          description: i18n.t('settingMutations.twilio'),
        })
      },
      onError: (error: Error) => {
        toast.closeAll()
        toast({
          description: error.message,
          status: 'danger',
        })
      },
    },
  )

  const mutateFormTwilioDeletion = useMutation(
    () => deleteTwilioCredentials(formId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(adminFormKeys.id(formId))
        toast.closeAll()
        // Show toast on success.
        toast({
          description: i18n.t('settingMutations.deleteTwilio'),
        })
      },
      onError: (error: Error) => {
        toast.closeAll()
        toast({
          description: error.message,
          status: 'danger',
        })
      },
    },
  )

  return {
    mutateFormTwilioDeletion,
    mutateFormTwilioDetails,
  }
}
